import { useCallback, useMemo, useRef } from 'react';
import { useApolloClient } from '@apollo/client';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useModals, ModalNames } from 'components/modals';
import useUI from 'hooks/use-ui';
import useUser from 'hooks/use-user';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useDutchiePay } from 'src/payments/hooks/use-dutchie-pay/use-dutchie-pay';
import { InterfaceVariants } from 'src/constants/interface-variants';
import { detectLoyaltyIntegration, Integrations, Statuses } from 'src/components/loyalty/use-loyalty';
import { formatBirthdayForRewards } from 'shared/helpers/rewards';
import { GetDutchiePosLoyaltyConfigDocument, GetRewardsV2Document } from 'src/gql/graphql';
import { useOnboardingFlowAnalytics, OnboardingModalContext } from 'src/hooks/use-onboarding-modal-analytics';
import type { OnboardingModalName } from 'src/utils/analytics/events';

const EXCLUDED_VARIANTS: InterfaceVariants[] = [
  InterfaceVariants.kiosk,
  InterfaceVariants.mobileEcomm,
  InterfaceVariants.plus,
];

export type TriggerPostSignupModalSequenceParams = {
  skipPostSignupSequence?: boolean;
  customerId?: string;
  phone?: string;
  birthday?: string;
};

export type UsePostSignupModalSequenceResult = {
  triggerPostSignupModalSequence: (params?: TriggerPostSignupModalSequenceParams) => Promise<void>;
  isReady: boolean;
};

export function usePostSignupModalSequence(): UsePostSignupModalSequenceResult {
  const { trackFlowStarted, logError } = useOnboardingFlowAnalytics({ source: 'postSignup' });
  const { openModal } = useModals();
  const isSequenceInFlightRef = useRef(false);
  const flags = useFlags();
  const { variant } = useUI();
  const User = useUser();
  const { dispensary, loading: dispensaryLoading } = useDispensary();
  const { isDutchiePayEnabledForDispo } = useDutchiePay();
  const apolloClient = useApolloClient();
  const dispensaryId = dispensary?.id ?? null;
  const isRolloutEnabled = flags['fintech.platform.post-signup-onboarding-modals.rollout'] ?? false;

  // Dutchie Loyalty is the only supported program for the join modal today.
  const showLoyaltyRewards = dispensary?.storeSettings?.showLoyaltyRewards ?? false;
  const isDutchieLoyaltyCandidate =
    isRolloutEnabled &&
    !EXCLUDED_VARIANTS.includes(variant) &&
    showLoyaltyRewards &&
    detectLoyaltyIntegration(dispensary) === Integrations.dutchiepos &&
    !!dispensaryId;

  // Bank Link eligibility is a synchronous derivation of the dispensary's payment methods
  // (via useDutchiePay), unlike loyalty's async GetRewardsV2 check above.
  const isBankLinkCandidate = isRolloutEnabled && !EXCLUDED_VARIANTS.includes(variant) && isDutchiePayEnabledForDispo;

  const shouldShowLoyaltyJoinModal = useCallback(
    async (customerId?: string, phone?: string, birthday?: string): Promise<boolean> => {
      if (!isDutchieLoyaltyCandidate || !customerId) {
        return false;
      }

      // Errors propagate to the caller, which logs them and treats the customer as ineligible
      // for the join step without aborting the rest of the sequence.
      const { data: configData } = await apolloClient.query({
        query: GetDutchiePosLoyaltyConfigDocument,
        variables: { dispensaryId },
        fetchPolicy: 'network-only',
      });

      if (!configData.getDutchiePosLoyaltyConfig?.loyaltyRequireOptIn) {
        return false;
      }

      const { data } = await apolloClient.query({
        query: GetRewardsV2Document,
        variables: {
          phoneNumber: phone ?? '',
          dispensaryId,
          pin: '',
          customerId,
          ...(birthday ? { dob: formatBirthdayForRewards(birthday) } : {}),
        },
        fetchPolicy: 'network-only',
      });

      return data.getRewardsV2?.isLoyaltyMember === false;
    },
    [isDutchieLoyaltyCandidate, apolloClient, dispensaryId]
  );

  const triggerPostSignupModalSequence = useCallback(
    async ({
      skipPostSignupSequence = false,
      customerId,
      phone,
      birthday,
    }: TriggerPostSignupModalSequenceParams = {}): Promise<void> => {
      if (skipPostSignupSequence || !isRolloutEnabled || EXCLUDED_VARIANTS.includes(variant)) {
        return;
      }

      // Guards against re-entrancy while a modal from a previous run is still open, which would
      // otherwise hang forever waiting on an already-active modal.
      if (isSequenceInFlightRef.current) {
        return;
      }
      isSequenceInFlightRef.current = true;

      try {
        // Set synchronously, before any awaited calls below, so LoyaltyAccountDetection's
        // async-gated effect can never observe this as unset for this signup.
        User.postSignupModalSequenceDispensaryId = dispensaryId;

        let isLoyaltyJoinEligible: boolean;
        try {
          isLoyaltyJoinEligible = await shouldShowLoyaltyJoinModal(customerId, phone, birthday);
        } catch (error) {
          logError('Failed to determine loyalty join eligibility', {
            dispensaryId,
            step: 'loyaltyJoin',
            error: error instanceof Error ? error.message : String(error),
          });
          isLoyaltyJoinEligible = false;
        }

        const eligibleSteps: OnboardingModalName[] = [];
        if (isLoyaltyJoinEligible) {
          eligibleSteps.push('loyaltyJoin');
        }
        if (isBankLinkCandidate) {
          eligibleSteps.push('bankLink');
        }
        // loyaltyPass can only ever appear after a successful join in this same run, so it's only
        // conditional (not ineligible outright) when the join itself is eligible.
        const conditionalSteps: OnboardingModalName[] = isLoyaltyJoinEligible ? ['loyaltyPass'] : [];

        trackFlowStarted({ eligibleSteps, conditionalSteps });

        let flowPosition = 0;
        const buildContext = (): OnboardingModalContext => {
          flowPosition += 1;
          return { source: 'postSignup', flowPosition };
        };

        let joinedLoyalty = false;
        if (isLoyaltyJoinEligible) {
          const joinResult = await openModal<OnboardingModalContext, { status: Statuses } | undefined>(
            ModalNames.postSignupLoyaltyJoin,
            buildContext()
          );
          joinedLoyalty = joinResult?.status === Statuses.success;
        }
        if (isBankLinkCandidate) {
          await openModal(ModalNames.postSignupBankLink, buildContext());
        }
        // The pass modal only fires immediately after a successful loyalty join in this same
        // sequence — never independently, and never after a skipped/ineligible/declined join.
        if (joinedLoyalty) {
          await openModal(ModalNames.postSignupLoyaltyPass, buildContext());
        }
      } finally {
        isSequenceInFlightRef.current = false;
      }
    },
    [
      openModal,
      isRolloutEnabled,
      variant,
      User,
      dispensaryId,
      shouldShowLoyaltyJoinModal,
      isBankLinkCandidate,
      trackFlowStarted,
      logError,
    ]
  );

  return useMemo(() => ({ triggerPostSignupModalSequence, isReady: !dispensaryLoading }), [
    triggerPostSignupModalSequence,
    dispensaryLoading,
  ]);
}
